import { Component, HostListener, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { LoginService } from '../login.service';
import { SidebarUiService } from './sidebar-ui.service';

// Minimum horizontal swipe distance (px) to trigger open.
const SWIPE_THRESHOLD = 70;
// Maximum distance from the left edge (px) for a swipe to count.
const EDGE_ZONE = 30;

// Route-driven sidebar container: chooses chrome (new/old via NewSidebar flag) and page nav (via route); chrome never unmounts so only the inner nav swaps.
@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: false,
})
export class SidebarComponent implements OnInit, OnDestroy {
  private readonly url = signal('');

  // Which page's nav body to show. Dashboard pages reuse the patients nav.
  readonly page = computed<'patients' | 'patexams' | 'exam' | null>(() => {
    const path = this.url().split('?')[0].split('#')[0];
    if (path === '/patsearch' || path.startsWith('/dashboard')) return 'patients';
    if (path === '/exams' || path === '/defaults') return 'patexams';
    if (path === '/exam') return 'exam';
    return null;
  });

  private destroy$ = new Subject<void>();
  private touchStartX = 0;
  private touchStartY = 0;

  constructor(
    public loginService: LoginService,
    public ui: SidebarUiService,
    private router: Router
  ) {}

  get newSidebar(): boolean {
    return this.loginService.newSidebar;
  }

  ngOnInit(): void {
    this.url.set(this.router.url);
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((e) => this.url.set(e.urlAfterRedirects));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:touchstart', ['$event'])
  onTouchStart(e: TouchEvent): void {
    this.touchStartX = e.touches[0].clientX;
    this.touchStartY = e.touches[0].clientY;
  }

  @HostListener('window:touchend', ['$event'])
  onTouchEnd(e: TouchEvent): void {
    const dx = e.changedTouches[0].clientX - this.touchStartX;
    const dy = e.changedTouches[0].clientY - this.touchStartY;

    // Swipe right from left edge → open
    if (
      dx > SWIPE_THRESHOLD &&
      Math.abs(dy) < SWIPE_THRESHOLD &&
      this.touchStartX < EDGE_ZONE &&
      !this.ui.mobileOpen()
    ) {
      this.ui.mobileOpen.set(true);
    }

    // Swipe left while open → close
    if (dx < -SWIPE_THRESHOLD && Math.abs(dy) < SWIPE_THRESHOLD && this.ui.mobileOpen()) {
      this.ui.closeMobile();
    }
  }
}
